import {
  getLocalStorageItem,
  removeLocalStorageItem,
} from '@/_utils/localstorage';

/**
 * Handle agent actions based on action type and data
 * @param action The action type
 * @param actionData The action data
 * @returns A response message
 */
export function handleAgentAction(action: string): string {
  switch (action) {
    case 'send_reminder':
      // Placeholder for sending reminder functionality
      return 'Reminder sent successfully!';
    case 'export_questionnaires':
      // Placeholder for exporting questionnaires functionality
      return 'Questionnaires exported successfully!';
    default:
      return 'Action not supported.';
  }
}

/**
 * Extract questionnaire and question data from graphql_data
 * @param graphql_data The graphql_data array from the AI response
 * @returns Object containing extracted questionnaire IDs, question IDs, and questionnaire pairs
 */
export function extractQuestionnaireData(graphql_data: any[]): {
  questionnaireIds: number[];
  questionIds: number[];
  questionnairePairs: Array<{
    questionnaire_id: number;
    question_id: number;
    text?: string;
    question_text?: string;
  }>;
} {
  const extractedQuestionnaireIds: number[] = [];
  const extractedQuestionIds: number[] = [];
  const extractedQuestionnairePairs: Array<{
    questionnaire_id: number;
    question_id: number;
    text?: string;
    question_text?: string;
  }> = [];

  if (graphql_data && Array.isArray(graphql_data)) {
    graphql_data.forEach((graphqlItem) => {
      // Handle searchQuestionnaireQuestions results
      if (
        graphqlItem.searchQuestionnaireQuestions &&
        Array.isArray(graphqlItem.searchQuestionnaireQuestions)
      ) {
        graphqlItem.searchQuestionnaireQuestions.forEach((question: any) => {
          if (question.id && question.questionnaireId) {
            const questionId = parseInt(question.id);
            const questionnaireId = parseInt(String(question.questionnaireId));

            if (!isNaN(questionId) && !isNaN(questionnaireId)) {
              extractedQuestionIds.push(questionId);
              extractedQuestionnaireIds.push(questionnaireId);
              extractedQuestionnairePairs.push({
                questionnaire_id: questionnaireId,
                question_id: questionId,
                text: question.text,
                question_text: question.text,
              });
            }
          }
        });
      }

      // Handle searchClientsByAnswer results
      if (
        graphqlItem.searchClientsByAnswer &&
        Array.isArray(graphqlItem.searchClientsByAnswer)
      ) {
        graphqlItem.searchClientsByAnswer.forEach((client: any) => {
          if (client.questionnaireId && client.questionIds) {
            const questionnaireId = parseInt(String(client.questionnaireId));

            if (!isNaN(questionnaireId)) {
              // Add questionnaire ID once per client
              if (!extractedQuestionnaireIds.includes(questionnaireId)) {
                extractedQuestionnaireIds.push(questionnaireId);
              }

              // Add each question ID and create pairs
              if (Array.isArray(client.questionIds)) {
                client.questionIds.forEach((qId: number, index: number) => {
                  const questionId = parseInt(String(qId));
                  if (!isNaN(questionId)) {
                    extractedQuestionIds.push(questionId);
                    extractedQuestionnairePairs.push({
                      questionnaire_id: questionnaireId,
                      question_id: questionId,
                      text: client.questions?.[index],
                      question_text: client.questions?.[index],
                    });
                  }
                });
              }
            }
          }
        });
      }
    });
  }

  return {
    questionnaireIds: extractedQuestionnaireIds,
    questionIds: extractedQuestionIds,
    questionnairePairs: extractedQuestionnairePairs,
  };
}

const getStorageKeys = (userId: string | number) => ({
  CHAT_THREADS: `soraban_chat_threads_${userId}`,
  ACTIVE_THREAD_ID: `soraban_active_thread_id_${userId}`,
});

/**
 * Clear chat threads from localStorage for a given user
 * @param userId The ID of the user whose chat history should be cleared
 */
export const clearChatThreadsFromCache = (userId: string | number) => {
  try {
    const keys = getStorageKeys(userId);
    const threads = getLocalStorageItem(keys.CHAT_THREADS);
    const activeThreadId = getLocalStorageItem(keys.ACTIVE_THREAD_ID);

    if (threads) {
      removeLocalStorageItem(keys.CHAT_THREADS);
    }
    if (activeThreadId) {
      removeLocalStorageItem(keys.ACTIVE_THREAD_ID);
    }
  } catch (error) {
    console.warn(`Failed to clear chat threads for user ${userId}:`, error);
  }
};
